import { getSequentialPalette, interpolatePalette } from '../../charts/shared/palettes';
import { J, JN, TITLE_SPACING, fileHeader } from './shared';

export function genHeatmap(chartType, config, t, data) {
  const palette = getSequentialPalette(config, t);

  // Build matrix
  const xSet = new Set();
  const ySet = new Set();
  data.forEach(d => { if (d.x !== undefined) xSet.add(d.x); if (d.y !== undefined) ySet.add(d.y); });
  const xLabels = Array.from(xSet);
  const yLabels = Array.from(ySet);
  const lookup = {};
  data.forEach(d => { lookup[d.y + '__' + d.x] = d.value; });
  const allVals = data.map(d => d.value).filter(v => v !== null && !isNaN(v));
  const minVal = allVals.length ? Math.min(...allVals) : 0;
  const maxVal = allVals.length ? Math.max(...allVals) : 1;

  // Pre-compute color gradient (256 steps)
  const gradient = interpolatePalette(palette, 256);

  const hasTitle = !!(config.title && String(config.title).trim());
  const showValues = !!config.showValues;
  const showCellBorder = !!config.showCellBorder;
  const decimals = config.valueDecimals ?? 1;
  const topMargin = hasTitle ? 45 : 20;

  return (
    fileHeader(['@visx/scale', '@visx/group', '@visx/axis'], 'heatmap') +
    `import React, { useMemo } from 'react';
import { scaleBand } from '@visx/scale';
import { Group } from '@visx/group';
import { AxisLeft } from '@visx/axis';

const X_LABELS = ${JN(xLabels)};
const Y_LABELS = ${JN(yLabels)};
const LOOKUP = ${JN(lookup)};
const MIN_VAL = ${minVal};
const MAX_VAL = ${maxVal};
const COLOR_GRADIENT = ${JN(gradient)};
const THEME = ${JN(t)};

function getColor(value) {
  if (value === null || value === undefined) return '#EEEEEE';
  const t = Math.max(0, Math.min(1, (value - MIN_VAL) / (MAX_VAL - MIN_VAL || 1)));
  return COLOR_GRADIENT[Math.min(255, Math.floor(t * 256))];
}

function getTextColor(value) {
  const t = (value - MIN_VAL) / (MAX_VAL - MIN_VAL || 1);
  return t > 0.55 ? '#FFFFFF' : '#000000';
}

export default function Chart({ width = 720, height = 480 }) {
  const margin = { top: ${topMargin}, right: 20, bottom: 80, left: 100 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const xScale = useMemo(() => scaleBand({ domain: X_LABELS, range: [0, innerWidth], padding: 0 }), [innerWidth]);
  const yScale = useMemo(() => scaleBand({ domain: Y_LABELS, range: [0, innerHeight], padding: 0 }), [innerHeight]);

  const cellWidth = xScale.bandwidth();
  const cellHeight = yScale.bandwidth();

  return (
    <div style={{ width, height, position: 'relative', backgroundColor: THEME.background }}>
      ${hasTitle ? `<div style={{ position: 'absolute', top: ${TITLE_SPACING.topInset}, left: 100, fontSize: 18, fontWeight: 400, color: THEME.titleColor }}>${String(config.title).replace(/</g, '&lt;')}</div>` : ''}
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
          {Y_LABELS.map((yLabel, yi) =>
            X_LABELS.map((xLabel, xi) => {
              const value = LOOKUP[yLabel + '__' + xLabel];
              const cellColor = getColor(value != null ? value : null);
              const cx = xScale(xLabel) || 0;
              const cy = yScale(yLabel) || 0;
              return (
                <g key={'cell-' + yi + '-' + xi}>
                  <rect x={cx} y={cy} width={cellWidth} height={cellHeight} fill={cellColor} stroke={${showCellBorder ? J('#FFFFFF') : J('none')}} strokeWidth={${showCellBorder ? 1 : 0}} />
                  ${showValues ? `{value != null && (
                    <text x={cx + cellWidth / 2} y={cy + cellHeight / 2} textAnchor="middle" dominantBaseline="middle" fontSize={Math.min(12, cellHeight * 0.35)} fill={getTextColor(value)}>{value.toFixed(${decimals})}</text>
                  )}` : ''}
                </g>
              );
            })
          )}
          <AxisLeft
            scale={yScale}
            stroke={THEME.axisColor}
            strokeWidth={THEME.spineWidth}
            tickStroke="none"
            tickLabelProps={(val) => ({ fill: THEME.tickColor, fontSize: 11, dx: -8, dy: cellHeight / 2, textAnchor: 'end' })}
            hideAxisLine={false}
            hideTicks
          />
          <g>
            {X_LABELS.map((label, xi) => {
              const cx = (xScale(label) || 0) + cellWidth / 2;
              const cy = innerHeight + 8;
              return (
                <text key={'xl-' + xi} x={cx} y={cy} textAnchor="end" fontSize={11} fill={THEME.tickColor} transform={'rotate(-40, ' + cx + ', ' + cy + ')'}>{label}</text>
              );
            })}
          </g>
          ${config.xlabel ? `<text x={innerWidth / 2} y={innerHeight + 65} textAnchor="middle" fontSize={13} fill={THEME.labelColor}>${config.xlabel}</text>` : ''}
          ${config.ylabel ? `<text x={-innerHeight / 2} y={-80} transform="rotate(-90)" textAnchor="middle" fontSize={13} fill={THEME.labelColor}>${config.ylabel}</text>` : ''}
        </Group>
      </svg>
    </div>
  );
}
`
  );
}
